import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "./Reveal";
import { useT } from "@/i18n";

const HERO_IMG = "/images/hero.webp";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const t = useT().hero;

  return (
    <section ref={ref} className="relative flex min-h-screen items-end overflow-hidden bg-ink" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt={t.imgAlt}
          fetchpriority="high"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/40 to-ink/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/30" />

      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-24 pt-40 lg:px-12 lg:pb-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="mb-8 flex items-center gap-4 text-[11px] uppercase tracking-[0.35em] text-bone/70"
          data-testid="hero-kicker"
        >
          <span className="h-px w-12 bg-terra" />
          {t.kicker}
        </motion.p>

        <h1 className="font-serif tracking-tight text-bone [text-shadow:0_2px_32px_rgba(0,0,0,0.55)]" data-testid="hero-heading">
          {t.lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block text-4xl sm:text-5xl lg:text-6xl leading-[1.08]"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.14, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 1 ? <em className="italic text-[#C9806B]">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-xl text-base leading-relaxed text-bone/80"
          data-testid="hero-subtext"
        >
          {t.subtext}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollToSection("#contact")}
            className="group flex items-center gap-3 bg-terra px-8 py-4 text-xs uppercase tracking-[0.22em] text-bone transition-colors duration-300 hover:bg-bone hover:text-ink"
          >
            {t.ctaPrimary}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
          <button
            data-testid="hero-cta-works"
            onClick={() => scrollToSection("#works")}
            className="border border-bone/40 px-8 py-4 text-xs uppercase tracking-[0.22em] text-bone transition-colors duration-300 hover:border-bone hover:bg-bone/10"
          >
            {t.ctaSecondary}
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden text-bone/60 lg:block"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};
